import { useState } from "react";
import { CheckCircle2, Circle, Plus } from "lucide-react";
import { useAuth } from "@/app/auth";
import { ACTIVITY_TYPE_LABEL } from "@/types";
import type { Enums } from "@/types";
import { ago, dateTime } from "@/lib/format";
import { useActivities, useAddActivity, useSaveTask, useTasks } from "../api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

type AType = Enums<"activity_type">;
const HUMAN_TYPES: AType[] = ["note", "call", "email", "meeting"];

export function ActivityTab({ clientId }: { clientId: string }) {
  const { user } = useAuth();
  const { data: activities } = useActivities(clientId);
  const { data: tasks } = useTasks(clientId);
  const add = useAddActivity(clientId);
  const saveTask = useSaveTask(clientId);
  const [type, setType] = useState<AType>("note");
  const [content, setContent] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");

  function submitActivity() {
    if (!content.trim()) return;
    add.mutate({ type, content: content.trim() }, { onSuccess: () => setContent("") });
  }
  function submitTask() {
    if (!taskTitle.trim()) return;
    saveTask.mutate({ values: { title: taskTitle.trim(), due_at: taskDue ? new Date(taskDue).toISOString() : null, assignee_id: user?.id ?? null } }, { onSuccess: () => { setTaskTitle(""); setTaskDue(""); } });
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
      <section>
        <div className="glass mb-4 rounded-2xl p-4">
          <div className="mb-2 flex items-center gap-2">
            <Select value={type} onValueChange={(v) => setType(v as AType)}>
              <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{HUMAN_TYPES.map((t) => <SelectItem key={t} value={t}>{ACTIVITY_TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">Une note, un appel, un email, une réunion…</span>
          </div>
          <Textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Qu'est-ce qui s'est passé ?" />
          <div className="mt-2 flex justify-end"><Button size="sm" onClick={submitActivity} disabled={add.isPending || !content.trim()}><Plus className="mr-1 h-4 w-4" />Ajouter</Button></div>
        </div>
        <ol className="space-y-3">
          {activities?.map((a) => {
            const who = (a.profiles as { full_name: string | null; email: string } | null);
            return (
              <li key={a.id} className="flex gap-3 text-sm">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{ACTIVITY_TYPE_LABEL[a.type]}</span>
                    {a.subject && <span>· {a.subject}</span>}
                    <span title={dateTime(a.occurred_at)}>· {ago(a.occurred_at)}</span>
                    {who && <span>· {who.full_name ?? who.email}</span>}
                  </div>
                  {a.content && <p className="mt-0.5 whitespace-pre-wrap">{a.content}</p>}
                </div>
              </li>
            );
          })}
          {activities && activities.length === 0 && <li className="text-sm text-muted-foreground">Aucune activité pour l'instant.</li>}
        </ol>
      </section>
      <aside>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Tâches</h3>
        <div className="glass mb-3 space-y-2 rounded-2xl p-3">
          <Input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Relancer pour le devis…" onKeyDown={(e) => e.key === "Enter" && submitTask()} />
          <div className="flex gap-2">
            <Input type="datetime-local" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="h-8" />
            <Button size="sm" onClick={submitTask} disabled={saveTask.isPending || !taskTitle.trim()}>Ajouter</Button>
          </div>
        </div>
        <ul className="space-y-1.5">
          {tasks?.map((t) => {
            const done = Boolean(t.completed_at);
            const late = !done && t.due_at && new Date(t.due_at) < new Date();
            return (
              <li key={t.id} className={cn("flex items-start gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50", done && "opacity-50")}>
                <button className="mt-0.5 text-muted-foreground hover:text-foreground" onClick={() => saveTask.mutate({ id: t.id, values: { completed_at: done ? null : new Date().toISOString() } })} title={done ? "Rouvrir" : "Terminer"}>
                  {done ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className={cn(done && "line-through")}>{t.title}</div>
                  {t.due_at && <div className={cn("text-xs text-muted-foreground", late && "text-destructive")}>{late ? "En retard · " : ""}{dateTime(t.due_at)}</div>}
                </div>
              </li>
            );
          })}
          {tasks && tasks.length === 0 && <li className="px-2 text-sm text-muted-foreground">Aucune tâche.</li>}
        </ul>
      </aside>
    </div>
  );
}
